import SimpleLightbox from 'simple-lightbox';
import 'simple-lightbox/dist/simpleLightbox.min.css';
import 'leaflet/dist/leaflet.css';

function getArchiveFilterConfig(form) {
    if (!(form instanceof HTMLFormElement)) {
        return null;
    }

    const restUrl = form.getAttribute('data-hfaf-rest-url') || '';
    const restNonce = form.getAttribute('data-hfaf-rest-nonce') || '';

    if (!restUrl) {
        return null;
    }

    return {
        restUrl,
        restNonce,
    };
}

let leafletModulePromise = null;

function getArchiveFiltersScope(element) {
    if (!(element instanceof Element)) {
        return null;
    }

    const blockWrapper = element.closest('.wp-block-highfive-archive-filters');

    if (blockWrapper && blockWrapper.parentElement) {
        return blockWrapper.parentElement;
    }

    return element.parentElement;
}

function getArchiveResultsForForm(form) {
    const scope = getArchiveFiltersScope(form);

    if (!(form instanceof HTMLFormElement) || !scope) {
        return null;
    }

    return scope.querySelector('.js-hf-archive-results');
}

function getArchiveRelatedCardForForm(form) {
    const scope = getArchiveFiltersScope(form);

    if (!(form instanceof HTMLFormElement) || !scope) {
        return null;
    }

    return scope.querySelector('.js-hf-archive-related-card');
}

function getArchiveResultsCountForForm(form) {
    const scope = getArchiveFiltersScope(form);

    if (!(form instanceof HTMLFormElement) || !scope) {
        return null;
    }

    return scope.querySelector('.js-hf-archive-results-count');
}

function getArchiveMapPanelForForm(form) {
    const scope = getArchiveFiltersScope(form);

    if (!(form instanceof HTMLFormElement) || !scope) {
        return null;
    }

    return scope.querySelector('.js-hf-archive-map-panel');
}

function getArchiveResetButtonsForForm(form) {
    const block = form instanceof HTMLFormElement
        ? form.closest('.wp-block-highfive-archive-filters')
        : null;

    return block ? Array.from(block.querySelectorAll('.js-hf-archive-filters-reset')) : [];
}

function getArchiveFormForResults(results) {
    const scope = getArchiveFiltersScope(results);

    if (!(results instanceof Element) || !scope) {
        return null;
    }

    return scope.querySelector('.js-hf-archive-filters');
}

function shouldUseArchiveCustomSelects(form) {
    return form instanceof HTMLFormElement
        && form.getAttribute('data-hfaf-use-custom-selects') === '1';
}

function shouldAutoRefreshArchiveFilters(form) {
    return form instanceof HTMLFormElement
        && form.getAttribute('data-hfaf-auto-refresh') === '1';
}

function isArchiveFiltersMobileViewport() {
    return window.matchMedia('(max-width: 781px)').matches;
}

function getArchiveFiltersDrawer(form) {
    if (!(form instanceof HTMLFormElement)) {
        return null;
    }

    return form.closest('.js-hf-archive-filters-drawer');
}

function getArchiveFiltersToggleButton(form) {
    const block = form instanceof HTMLFormElement
        ? form.closest('.wp-block-highfive-archive-filters')
        : null;

    return block ? block.querySelector('.js-hf-archive-filters-toggle') : null;
}

function openArchiveFiltersDrawer(form) {
    const drawer = getArchiveFiltersDrawer(form);
    const toggleButton = getArchiveFiltersToggleButton(form);
    const panel = drawer ? drawer.querySelector('.hf-archive-filters') : null;

    if (!drawer || !isArchiveFiltersMobileViewport()) {
        return;
    }

    drawer.hidden = false;
    drawer.classList.remove('is-closing');

    if (!(panel instanceof HTMLElement)) {
        drawer.classList.add('is-open');
    } else {
        panel.getBoundingClientRect();

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                if (!drawer.hidden) {
                    drawer.classList.add('is-open');
                }
            });
        });
    }

    if (toggleButton instanceof HTMLElement) {
        toggleButton.setAttribute('aria-expanded', 'true');
    }

    document.documentElement.classList.add('has-hf-archive-filters-open');
}

function closeArchiveFiltersDrawer(form) {
    const drawer = getArchiveFiltersDrawer(form);
    const toggleButton = getArchiveFiltersToggleButton(form);

    if (!drawer) {
        return;
    }

    drawer.classList.remove('is-open');
    drawer.classList.add('is-closing');

    if (toggleButton instanceof HTMLElement) {
        toggleButton.setAttribute('aria-expanded', 'false');
    }

    document.documentElement.classList.remove('has-hf-archive-filters-open');

    if (!isArchiveFiltersMobileViewport()) {
        drawer.classList.remove('is-closing');
        drawer.hidden = false;
        return;
    }

    const panel = drawer.querySelector('.hf-archive-filters');

    if (!(panel instanceof HTMLElement)) {
        drawer.classList.remove('is-closing');
        drawer.hidden = true;
        return;
    }

    const handleTransitionEnd = (event) => {
        if (event.target !== panel || event.propertyName !== 'transform') {
            return;
        }

        drawer.hidden = true;
        drawer.classList.remove('is-closing');
        panel.removeEventListener('transitionend', handleTransitionEnd);
    };

    panel.addEventListener('transitionend', handleTransitionEnd);
}

function syncArchiveFiltersDrawerForViewport(form) {
    const drawer = getArchiveFiltersDrawer(form);
    const toggleButton = getArchiveFiltersToggleButton(form);

    if (!drawer) {
        return;
    }

    if (isArchiveFiltersMobileViewport()) {
        if (!drawer.classList.contains('is-open')) {
            drawer.hidden = true;
        }
    } else {
        drawer.hidden = false;
        drawer.classList.remove('is-open');
        drawer.classList.remove('is-closing');

        if (toggleButton instanceof HTMLElement) {
            toggleButton.setAttribute('aria-expanded', 'false');
        }

        document.documentElement.classList.remove('has-hf-archive-filters-open');
    }
}

function closeArchiveCustomSelects(scope = document, except = null) {
    scope.querySelectorAll('.js-hf-archive-custom-select').forEach((customSelect) => {
        if (except && customSelect === except) {
            return;
        }

        customSelect.classList.remove('is-open');
        const input = customSelect.querySelector('.js-hf-archive-custom-select-input');
        const dropdown = customSelect.querySelector('.js-hf-archive-custom-select-dropdown');

        if (input instanceof HTMLInputElement) {
            input.setAttribute('aria-expanded', 'false');
            input.value = customSelect.getAttribute('data-selected-label') || '';
        }

        if (dropdown instanceof HTMLElement) {
            dropdown.hidden = true;
        }

        filterArchiveCustomSelectOptions(customSelect, '');
    });
}

function filterArchiveCustomSelectOptions(customSelect, query) {
    if (!(customSelect instanceof Element)) {
        return;
    }

    const normalizedQuery = (query || '').trim().toLowerCase();
    let visibleCount = 0;

    customSelect.querySelectorAll('.js-hf-archive-custom-select-option').forEach((optionElement) => {
        const optionLabel = (optionElement.textContent || '').trim().toLowerCase();
        const matches = normalizedQuery === '' || optionLabel.includes(normalizedQuery);

        optionElement.hidden = !matches;

        if (matches) {
            visibleCount += 1;
        }
    });

    const empty = customSelect.querySelector('.js-hf-archive-custom-select-empty');

    if (empty) {
        empty.hidden = visibleCount > 0;
    }
}

function syncArchiveCustomSelect(select) {
    if (!(select instanceof HTMLSelectElement)) {
        return;
    }

    const customSelect = select.parentElement
        ? select.parentElement.querySelector('.js-hf-archive-custom-select')
        : null;

    if (!customSelect) {
        return;
    }

    const input = customSelect.querySelector('.js-hf-archive-custom-select-input');
    const options = customSelect.querySelectorAll('.js-hf-archive-custom-select-option');
    const selectedOption = Array.from(select.options).find((option) => option.value === select.value) || select.options[0];

    if (input instanceof HTMLInputElement && selectedOption) {
        const selectedLabel = selectedOption.textContent || '';

        customSelect.setAttribute('data-selected-label', selectedLabel);
        input.value = selectedLabel;
    }

    options.forEach((optionElement) => {
        const isSelected = optionElement.getAttribute('data-value') === select.value;

        optionElement.classList.toggle('is-selected', isSelected);
        optionElement.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
}

function createArchiveCustomSelect(select) {
    if (!(select instanceof HTMLSelectElement) || select.dataset.hfArchiveCustomSelectBound === 'true') {
        return;
    }

    select.dataset.hfArchiveCustomSelectBound = 'true';
    select.classList.add('hf-archive-filters__native-select');

    const customSelect = document.createElement('div');
    const field = document.createElement('div');
    const input = document.createElement('input');
    const chevron = document.createElement('span');
    let skipNextFocusOpen = false;
    const dropdown = document.createElement('div');

    customSelect.className = 'hf-archive-custom-select js-hf-archive-custom-select';
    field.className = 'hf-archive-custom-select__field';
    input.type = 'text';
    input.className = 'hf-archive-custom-select__input js-hf-archive-custom-select-input';
    input.style.setProperty('text-align', 'left', 'important');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('placeholder', 'Rechercher...');
    chevron.className = 'hf-archive-custom-select__chevron';
    chevron.setAttribute('aria-hidden', 'true');

    dropdown.className = 'hf-archive-custom-select__dropdown';
    dropdown.classList.add('js-hf-archive-custom-select-dropdown');
    dropdown.setAttribute('role', 'listbox');
    dropdown.hidden = true;
    dropdown.innerHTML = `
        <div class="hf-archive-custom-select__options js-hf-archive-custom-select-options" role="presentation"></div>
        <p class="hf-archive-custom-select__empty js-hf-archive-custom-select-empty" hidden>Aucun resultat</p>
    `;

    const optionsContainer = dropdown.querySelector('.js-hf-archive-custom-select-options');

    Array.from(select.options).forEach((option) => {
        const optionElement = document.createElement('div');

        optionElement.className = 'hf-archive-custom-select__option js-hf-archive-custom-select-option';
        optionElement.setAttribute('role', 'option');
        optionElement.setAttribute('tabindex', '0');
        optionElement.setAttribute('data-value', option.value);
        optionElement.textContent = option.textContent || '';

        const commitOption = () => {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            syncArchiveCustomSelect(select);
            closeArchiveCustomSelects(document);
            skipNextFocusOpen = true;
            input.focus();
        };

        if (optionsContainer) {
            optionsContainer.appendChild(optionElement);
        }

        optionElement.addEventListener('click', commitOption);
        optionElement.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                commitOption();
            }
        });
    });

    function openCustomSelect() {
        closeArchiveCustomSelects(document, customSelect);
        customSelect.classList.add('is-open');
        input.setAttribute('aria-expanded', 'true');
        dropdown.hidden = false;
    }

    input.addEventListener('focus', () => {
        if (skipNextFocusOpen) {
            skipNextFocusOpen = false;
            return;
        }
        openCustomSelect();
    });

    input.addEventListener('click', () => {
        openCustomSelect();
    });

    input.addEventListener('input', () => {
        openCustomSelect();
        filterArchiveCustomSelectOptions(customSelect, input.value);
    });

    input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            closeArchiveCustomSelects(document);
            input.blur();
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            openCustomSelect();
            const firstVisibleOption = customSelect.querySelector('.js-hf-archive-custom-select-option:not([hidden])');

            if (firstVisibleOption instanceof HTMLElement) {
                firstVisibleOption.focus();
            }
        }
    });

    dropdown.addEventListener('keydown', (event) => {
        if (!(event.target instanceof HTMLElement) || !event.target.classList.contains('js-hf-archive-custom-select-option')) {
            return;
        }

        const visibleOptions = Array.from(
            customSelect.querySelectorAll('.js-hf-archive-custom-select-option:not([hidden])')
        );
        const currentIndex = visibleOptions.indexOf(event.target);

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            const nextOption = visibleOptions[currentIndex + 1] || visibleOptions[0];

            if (nextOption instanceof HTMLElement) {
                nextOption.focus();
            }
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            const previousOption = visibleOptions[currentIndex - 1] || input;

            if (previousOption instanceof HTMLElement) {
                previousOption.focus();
            }
        } else if (event.key === 'Escape') {
            event.preventDefault();
            closeArchiveCustomSelects(document);
            input.focus();
        }
    });

    select.addEventListener('change', () => {
        syncArchiveCustomSelect(select);
    });

    field.appendChild(input);
    field.appendChild(chevron);
    customSelect.appendChild(field);
    customSelect.appendChild(dropdown);
    select.insertAdjacentElement('afterend', customSelect);
    syncArchiveCustomSelect(select);
    filterArchiveCustomSelectOptions(customSelect, '');
}

function initArchiveCustomSelects(scope) {
    if (!(scope instanceof Element) && !(scope instanceof Document)) {
        return;
    }

    const forms = scope instanceof HTMLFormElement
        ? [ scope ]
        : Array.from(scope.querySelectorAll('.js-hf-archive-filters'));

    forms
        .filter((form) => shouldUseArchiveCustomSelects(form))
        .forEach((form) => {
            form.querySelectorAll('select').forEach((select) => {
                createArchiveCustomSelect(select);
                syncArchiveCustomSelect(select);
            });
        });
}

function formatResultsCount(count) {
    const total = Math.max(0, parseInt(count, 10) || 0);

    return total > 1
        ? `${total} résultats`
        : `${total} résultat`;
}

function updateResultsCount(form, count) {
    const countElement = getArchiveResultsCountForForm(form);

    if (countElement) {
        countElement.textContent = formatResultsCount(count);
    }
}

function isArchiveMapOpen(form) {
    return !!form.__hfArchiveMapLightbox;
}

function hasArchiveMapBeenInitialized(form) {
    return !!form.__hfArchiveMapContext;
}

function getArchiveMapRequestKey(form) {
    return buildArchiveRequestParams(form, {
        includeMapData: true,
    }).toString();
}

function getCachedArchiveMapData(form, requestKey) {
    const cache = form.__hfArchiveMapDataCache;

    if (!cache || cache.requestKey !== requestKey) {
        return null;
    }

    return cache.data || null;
}

function setCachedArchiveMapData(form, requestKey, data) {
    form.__hfArchiveMapDataCache = {
        requestKey,
        data,
    };
}

function syncFormWithQueryLoop(form) {
    const results = getArchiveResultsForForm(form);

    if (!results) {
        return;
    }

    const perPage = results.getAttribute('data-hf-per-page');
    const postType = results.getAttribute('data-hf-post-type');
    const sticky = results.getAttribute('data-hf-sticky');
    const perPageField = form.elements.namedItem('perPage');
    const postTypeField = form.elements.namedItem('postType');
    const stickyField = form.elements.namedItem('sticky');

    if (perPage && perPageField) {
        perPageField.value = perPage;
    }

    if (postType && postTypeField) {
        postTypeField.value = postType;
    }

    if (stickyField) {
        stickyField.value = sticky || '';
    }
}

function buildArchiveStateFromForm(form) {
    const data = new FormData(form);
    const state = {};

    data.forEach((value, key) => {
        state[key] = typeof value === 'string' ? value : '';
    });

    return state;
}

function hasActiveArchiveFilters(form) {
    const state = buildArchiveStateFromForm(form);

    return Object.entries(state).some(([key, value]) => {
        if (key === 'search') {
            return value.trim() !== '';
        }

        return (key.indexOf('tax_') === 0 || key.indexOf('acf_') === 0) && value !== '';
    });
}

function syncArchiveResetButtonVisibility(form) {
    if (!(form instanceof HTMLFormElement)) {
        return;
    }
    const hasActiveFilters = !hasActiveArchiveFilters(form);

    getArchiveResetButtonsForForm(form).forEach((resetButton) => {
        if (resetButton instanceof HTMLElement) {
            resetButton.hidden = hasActiveFilters;
        }
    });
}

function getTrackedFilterKeys(source) {
    const keys = new Set([ 'search', 'page' ]);

    function trackFilterKey(key) {
        if (
            typeof key === 'string'
            && ( key.indexOf('tax_') === 0 || key.indexOf('acf_') === 0 )
        ) {
            keys.add(key);
        }
    }

    if (source instanceof HTMLFormElement) {
        Array.from(source.elements).forEach((field) => {
            if (field && field.name) {
                trackFilterKey(field.name);
            }
        });
    }

    if (source instanceof URL) {
        source.searchParams.forEach((value, key) => {
            trackFilterKey(key);
        });
    }

    return Array.from(keys);
}

function syncArchiveUrl(form) {
    const url = new URL(window.location.href);
    const state = buildArchiveStateFromForm(form);
    const trackedKeys = getTrackedFilterKeys(form);

    trackedKeys.forEach((key) => {
        const value = state[key] || '';

        if (value === '' || (key === 'page' && String(value) === '1')) {
            url.searchParams.delete(key);
            return;
        }

        url.searchParams.set(key, value);
    });

    window.history.pushState({ archiveFilters: true }, '', url.toString());
}

function applyStateToForm(form, state) {
    getTrackedFilterKeys(form).forEach((key) => {
        if (typeof state[key] === 'undefined') {
            return;
        }

        const field = form.elements.namedItem(key);

        if (!field) {
            return;
        }

        field.value = state[key];
    });

    syncArchiveResetButtonVisibility(form);
}

function getStateFromUrl() {
    const url = new URL(window.location.href);
    const state = {
        search: url.searchParams.get('search') || '',
        page: url.searchParams.get('page') || '1',
    };

    getTrackedFilterKeys(url).forEach((key) => {
        if (key === 'search' || key === 'page') {
            return;
        }

        state[key] = url.searchParams.get(key) || '';
    });

    return state;
}

function getPageFromPaginationLink(link) {
    if (!(link instanceof HTMLAnchorElement)) {
        return '1';
    }

    const url = new URL(link.href, window.location.origin);

    // 1. priorité query string (?page=2)
    const queryPage = url.searchParams.get('page');
    if (queryPage) {
        return queryPage;
    }

    // 2. fallback /page/2/
    const match = url.pathname.match(/\/page\/(\d+)\/?$/);
    if (match && match[1]) {
        return match[1];
    }

    // 3. fallback texte
    const textPage = (link.textContent || '').trim();
    if (/^\d+$/.test(textPage)) {
        return textPage;
    }

    return '1';
}

function buildArchiveRequestParams(form, options = {}) {
    const results = getArchiveResultsForForm(form);
    const params = new URLSearchParams(new FormData(form));
    const queryTemplate = results ? results.getAttribute('data-hf-query-template') : '';

    if (queryTemplate) {
        params.set('queryTemplate', queryTemplate);
    }

    if (options.includeMapData) {
        params.set('includeMapData', '1');
    }

    return params;
}

function loadLeafletModule() {
    if (leafletModulePromise) {
        return leafletModulePromise;
    }

    leafletModulePromise = import('leaflet').then((module) => module.default || module);

    return leafletModulePromise;
}

function createArchiveMapRoot(count) {
    const root = document.createElement('div');

    root.className = 'hf-archive-map-lightbox';
    root.innerHTML = `
        <div class="hf-archive-map-lightbox__viewport">
            <div class="hf-archive-map-lightbox__loader js-hf-archive-map-loader" hidden>
                <span class="hf-archive-map-lightbox__spinner" aria-hidden="true"></span>
                <p class="hf-archive-map-lightbox__loading-text">Chargement de la carte…</p>
            </div>
            <div class="hf-archive-map-lightbox__canvas js-hf-archive-map-canvas"></div>
        </div>
        <p class="hf-archive-map-lightbox__empty js-hf-archive-map-empty" hidden>Aucun lieu à afficher pour ces résultats.</p>
    `;

    return root;
}

async function ensureArchiveMapContext(form) {
    if (form.__hfArchiveMapContext) {
        return form.__hfArchiveMapContext;
    }

    const panel = getArchiveMapPanelForForm(form);

    if (!panel) {
        return null;
    }

    const count = getArchiveResultsForForm(form)
        ? getArchiveResultsForForm(form).getAttribute('data-hf-found-posts') || '0'
        : '0';
    const L = await loadLeafletModule();
    const root = createArchiveMapRoot(count);

    panel.innerHTML = '';
    panel.appendChild(root);
    panel.hidden = true;

    form.__hfArchiveMapContext = {
        L,
        panel,
        root,
        canvas: root.querySelector('.js-hf-archive-map-canvas'),
        loader: root.querySelector('.js-hf-archive-map-loader'),
        empty: root.querySelector('.js-hf-archive-map-empty'),
        map: null,
        layer: null,
    };

    return form.__hfArchiveMapContext;
}

function setArchiveMapLoading(form, isLoading) {
    const mapContext = form.__hfArchiveMapContext;

    if (!mapContext || !mapContext.root) {
        return;
    }

    mapContext.root.classList.toggle('is-loading', isLoading);

    if (mapContext.loader) {
        mapContext.loader.hidden = !isLoading;
    }
}

function ensureArchiveMapInstance(form) {
    const mapContext = form.__hfArchiveMapContext;

    if (!mapContext) {
        return null;
    }

    if (mapContext.map) {
        return mapContext.map;
    }

    const canvas = mapContext.canvas;

    if (!canvas || !mapContext.L) {
        return null;
    }

    const map = mapContext.L.map(canvas, {
        zoomControl: true,
        dragging: true,
        scrollWheelZoom: true,
        touchZoom: true,
        doubleClickZoom: true,
        boxZoom: true,
        keyboard: true,
    }).setView([49.1829, -0.3707], 8);

    mapContext.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
    }).addTo(map);

    mapContext.map = map;
    mapContext.layer = mapContext.L.layerGroup().addTo(map);

    return map;
}

function buildMapPopupContent(marker) {
    const events = Array.isArray(marker.events) ? marker.events.slice(0, 5) : [];
    const list = events.length > 0
        ? `<ul class="hf-archive-map__popup-list">${events.map((event) => `<li><a href="${event.permalink}">${event.title}</a></li>`).join('')}</ul>`
        : '';
    const title = marker.permalink != ''
        ? `<h3 class="hf-archive-map__popup-title"><a href="${marker.permalink}">${marker.title}</a></h3>`
        : `<h3 class="hf-archive-map__popup-title">${marker.title}</h3>`;

    return `<div class="hf-archive-map__popup">${title}${list}</div>`;
}

function syncArchiveMap(form, markers, foundPosts) {
    const mapContext = form.__hfArchiveMapContext;
    const map = ensureArchiveMapInstance(form);

    if (!mapContext || !map) {
        return;
    }

    const layer = mapContext.layer;
    const empty = mapContext.empty;
    const countElement = mapContext.root
        ? mapContext.root.querySelector('.js-hf-archive-map-count')
        : null;
    const normalizedMarkers = Array.isArray(markers) ? markers : [];

    updateResultsCount(form, foundPosts);

    if (countElement) {
        countElement.textContent = formatResultsCount(foundPosts);
    }

    layer.clearLayers();

    if (normalizedMarkers.length === 0) {
        if (empty) {
            empty.hidden = false;
        }

        map.setView([49.1829, -0.3707], 8);
        map.invalidateSize();
        return;
    }

    if (empty) {
        empty.hidden = true;
    }

    const bounds = [];

    normalizedMarkers.forEach((marker) => {
        if (
            typeof marker.lat !== 'number'
            || typeof marker.lng !== 'number'
        ) {
            return;
        }

        const leafletMarker = mapContext.L.circleMarker([marker.lat, marker.lng], {
            radius: 5,
            weight: 3.5,
            color: '#111111',
            fillColor: '#ffffff',
            fillOpacity: 1,
        });
        leafletMarker.bindPopup(buildMapPopupContent(marker));
        leafletMarker.addTo(layer);
        bounds.push([marker.lat, marker.lng]);
    });

    map.setMinZoom(1);

    if (bounds.length === 1) {
        map.setView(bounds[0], 12);
        map.setMinZoom(12);
    } else if (bounds.length > 1) {
        map.fitBounds(bounds, { padding: [40, 40] });
        map.once('moveend', () => {
            map.setMinZoom(map.getZoom());
        });
    }

    window.setTimeout(() => {
        map.invalidateSize();
    }, 50);
}

async function fetchArchiveMapData(form) {
    const config = getArchiveFilterConfig(form);

    if (!config) {
        return null;
    }

    const params = buildArchiveRequestParams(form, {
        includeMapData: true,
    });
    const response = await window.fetch(config.restUrl, {
        method: 'POST',
        headers: {
            'X-WP-Nonce': config.restNonce,
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body: params.toString(),
    });

    if (!response.ok) {
        throw new Error(`Archive map request failed: ${response.status}`);
    }

    return response.json();
}

async function openArchiveMap(form) {
    const requestKey = getArchiveMapRequestKey(form);
    const mapContext = await ensureArchiveMapContext(form);

    if (!mapContext) {
        return;
    }

    if (mapContext.panel) {
        mapContext.panel.hidden = false;
    }

    form.__hfArchiveMapLightbox = SimpleLightbox.open({
        content: mapContext.root,
        closeBtnCaption: 'Fermer',
        beforeClose: () => {
            const currentMapContext = form.__hfArchiveMapContext;

            if (currentMapContext && currentMapContext.panel && currentMapContext.root) {
                currentMapContext.panel.appendChild(currentMapContext.root);
                currentMapContext.panel.hidden = true;
            }

            form.__hfArchiveMapLightbox = null;
            document.documentElement.classList.remove('has-hf-archive-map-open');
        },
    });

    document.documentElement.classList.add('has-hf-archive-map-open');

    try {
        let data = getCachedArchiveMapData(form, requestKey);

        if (!data) {
            setArchiveMapLoading(form, true);
            data = await fetchArchiveMapData(form);

            if (data) {
                setCachedArchiveMapData(form, requestKey, data);
            }
        }

        if (data) {
            syncArchiveMap(form, data.mapMarkers || [], data.foundPosts || 0);
        }
    } catch (error) {
        // Keep the panel open; just log the issue for dev debugging.
        console.error('[Highfive FSE] Archive map error', error);
    } finally {
        setArchiveMapLoading(form, false);
    }
}

function closeArchiveMap(form) {
    if (form.__hfArchiveMapLightbox && typeof form.__hfArchiveMapLightbox.close === 'function') {
        form.__hfArchiveMapLightbox.close();
    }

    form.__hfArchiveMapLightbox = null;
    document.documentElement.classList.remove('has-hf-archive-map-open');
}

async function updateArchiveResults(form, options = {}) {
    const config = getArchiveFilterConfig(form);
    const results = getArchiveResultsForForm(form);
    const relatedCard = getArchiveRelatedCardForForm(form);
    const includeMapData = isArchiveMapOpen(form) || hasArchiveMapBeenInitialized(form);
    const shouldScrollToResults = options.scrollToResults === true;

    if (!config || !results) {
        return;
    }

    const params = buildArchiveRequestParams(form, {
        includeMapData,
    });

    form.classList.add('is-loading');
    results.classList.add('is-loading');

    try {
        const response = await window.fetch(config.restUrl, {
            method: 'POST',
            headers: {
                'X-WP-Nonce': config.restNonce,
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: params.toString(),
        });

        if (!response.ok) {
            throw new Error(`Archive filter request failed: ${response.status}`);
        }

        const data = await response.json();

        if (data && typeof data.html === 'string') {
            results.innerHTML = data.html + ( data.pagination || '' );
            results.setAttribute('data-hf-found-posts', String(data.foundPosts || 0));

            if (relatedCard && typeof data.relatedCard === 'string') {
                relatedCard.innerHTML = data.relatedCard;
            }

            updateResultsCount(form, data.foundPosts || 0);

            if (includeMapData) {
                setCachedArchiveMapData(form, getArchiveMapRequestKey(form), data);

                if (isArchiveMapOpen(form)) {
                    syncArchiveMap(form, data.mapMarkers || [], data.foundPosts || 0);
                }
            }

            syncArchiveUrl(form);
            syncArchiveResetButtonVisibility(form);

            if (shouldScrollToResults) {
                results.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        }
    } catch (error) {
        // Keep the current listing in place and log for dev debugging.
        console.error('[Highfive FSE] Archive filters error', error);
    } finally {
        form.classList.remove('is-loading');
        results.classList.remove('is-loading');
    }
}

function triggerArchiveResultsRefresh(form) {
    const pageField = form.elements.namedItem('page');

    if (pageField) {
        pageField.value = '1';
    }

    syncArchiveResetButtonVisibility(form);
    updateArchiveResults(form);

    if (isArchiveFiltersMobileViewport()) {
        closeArchiveFiltersDrawer(form);
    }
}

export function initArchiveFilters(root = document) {
    const forms = root.querySelectorAll('.js-hf-archive-filters');

    forms.forEach((form) => {
        if (form.dataset.hfArchiveFiltersBound === 'true') {
            return;
        }

        form.dataset.hfArchiveFiltersBound = 'true';
        syncFormWithQueryLoop(form);
        applyStateToForm(form, getStateFromUrl());
        initArchiveCustomSelects(form);
        syncArchiveFiltersDrawerForViewport(form);

        const results = getArchiveResultsForForm(form);
        if (results) {
            updateResultsCount(form, results.getAttribute('data-hf-found-posts') || '0');
        }
        syncArchiveResetButtonVisibility(form);

        if (shouldAutoRefreshArchiveFilters(form)) {
            let searchDebounceId = null;

            form.querySelectorAll('select, input[type="date"]').forEach((field) => {
                field.addEventListener('change', () => {
                    if (isArchiveFiltersMobileViewport()) {
                        syncArchiveResetButtonVisibility(form);
                        return;
                    }

                    triggerArchiveResultsRefresh(form);
                });
            });

            const searchField = form.elements.namedItem('search');

            if (searchField instanceof HTMLInputElement) {
                searchField.addEventListener('input', () => {
                    if (isArchiveFiltersMobileViewport()) {
                        syncArchiveResetButtonVisibility(form);
                        return;
                    }

                    window.clearTimeout(searchDebounceId);
                    searchDebounceId = window.setTimeout(() => {
                        triggerArchiveResultsRefresh(form);
                    }, 250);
                });
            }
        }

        form.addEventListener('submit', (event) => {
            event.preventDefault();
            triggerArchiveResultsRefresh(form);
        });

        getArchiveResetButtonsForForm(form).forEach((resetButton) => {
            resetButton.addEventListener('click', () => {
                form.reset();
                window.setTimeout(() => {
                    initArchiveCustomSelects(form);
                }, 0);
                const pageField = form.elements.namedItem('page');

                if (pageField) {
                    pageField.value = '1';
                }

                updateArchiveResults(form);
                syncArchiveResetButtonVisibility(form);
            });
        });

        const scope = getArchiveFiltersScope(form);
        const drawerToggleButton = scope
            ? scope.querySelector('.js-hf-archive-filters-toggle')
            : null;
        const drawerCloseButtons = scope
            ? scope.querySelectorAll('.js-hf-archive-filters-close')
            : [];
        const mapToggleButton = scope
            ? scope.querySelector('.js-hf-archive-map-toggle')
            : null;

        if (drawerToggleButton) {
            drawerToggleButton.addEventListener('click', () => {
                const drawer = getArchiveFiltersDrawer(form);
                const isOpen = drawer ? drawer.classList.contains('is-open') : false;

                if (isOpen) {
                    closeArchiveFiltersDrawer(form);
                    return;
                }

                openArchiveFiltersDrawer(form);
            });
        }

        drawerCloseButtons.forEach((button) => {
            button.addEventListener('click', () => {
                closeArchiveFiltersDrawer(form);
            });
        });

        if (mapToggleButton) {
            mapToggleButton.addEventListener('click', () => {
                if (isArchiveMapOpen(form)) {
                    closeArchiveMap(form);
                    return;
                }

                openArchiveMap(form);
            });
        }

    });

    if (forms.length > 0 && !window.__highfiveArchiveFiltersPopstateBound) {
        window.__highfiveArchiveFiltersPopstateBound = true;

        window.addEventListener('popstate', () => {
            document.querySelectorAll('.js-hf-archive-filters').forEach((form) => {
                applyStateToForm(form, getStateFromUrl());
                initArchiveCustomSelects(form);
                updateArchiveResults(form);
            });
        });
    }

    if (forms.length > 0 && !window.__highfiveArchiveFiltersPaginationBound) {
        window.__highfiveArchiveFiltersPaginationBound = true;

        document.addEventListener('click', (event) => {
            const target = event.target instanceof Element
                ? event.target.closest('.hf-archive-results__pagination a')
                : null;

            if (!target) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            const results = target.closest('.js-hf-archive-results');
            const form = getArchiveFormForResults(results);

            if (!form) {
                return;
            }

            const pageField = form.elements.namedItem('page');
            let nextPage = getPageFromPaginationLink(target);

            if (nextPage === '1' && pageField) {
                const currentPage = parseInt(pageField.value || '1', 10) || 1;
                const rel = (target.getAttribute('rel') || '').toLowerCase();
                const ariaLabel = (target.getAttribute('aria-label') || '').toLowerCase();

                if (rel.includes('next') || ariaLabel.includes('suivant') || ariaLabel.includes('next')) {
                    nextPage = String(currentPage + 1);
                } else if (rel.includes('prev') || ariaLabel.includes('précédent') || ariaLabel.includes('precedent') || ariaLabel.includes('previous')) {
                    nextPage = String(Math.max(1, currentPage - 1));
                }
            }

            if (pageField) {
                pageField.value = nextPage;
            }

            updateArchiveResults(form, {
                scrollToResults: true,
            });
        }, true);
    }

    if (forms.length > 0 && !window.__highfiveArchiveFiltersCustomSelectsBound) {
        window.__highfiveArchiveFiltersCustomSelectsBound = true;

        document.addEventListener('click', (event) => {
            if (event.target instanceof Element && event.target.closest('.js-hf-archive-custom-select')) {
                return;
            }

            closeArchiveCustomSelects(document);
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                closeArchiveCustomSelects(document);

                document.querySelectorAll('.js-hf-archive-filters').forEach((form) => {
                    closeArchiveFiltersDrawer(form);
                });
            }
        });
    }

    if (forms.length > 0 && !window.__highfiveArchiveFiltersResizeBound) {
        window.__highfiveArchiveFiltersResizeBound = true;

        window.addEventListener('resize', () => {
            document.querySelectorAll('.js-hf-archive-filters').forEach((form) => {
                syncArchiveFiltersDrawerForViewport(form);
            });
        });
    }
}

function normalizePaginationUrlOnLoad() {
    const url = new URL(window.location.href);

    // Cas 1 : /page/2/ dans le pathname
    const match = url.pathname.match(/\/page\/(\d+)\/?$/);

    if (!match) return;

    const page = match[1];

    // Nettoie le pathname (/page/X -> /programme/)
    const newPathname = url.pathname.replace(/\/page\/\d+\/?$/, '/');

    // Reconstruit l'URL propre
    url.pathname = newPathname.endsWith('/') ? newPathname : newPathname + '/';
    url.searchParams.set('page', page);

    // Remplace sans ajouter d'entrée dans l'historique
    window.history.replaceState({}, '', url.toString());
}

document.addEventListener('DOMContentLoaded', () => {
    initArchiveFilters();
    normalizePaginationUrlOnLoad();
});
